import { Option } from 'effect';

import { MAX_MARKER_INDEX } from '@/lib/i18nOverrides/markers';

export type PrimitiveValue = string | number | boolean | null | undefined;

export interface MarkerEntry {
  readonly id: string;
  readonly defaultMessage?: string;
  /** Primitive values only — functions and elements (rich-text tags) are not kept. */
  readonly values?: Readonly<Record<string, PrimitiveValue>>;
}

export interface MarkerRegistry {
  /** Index of the entry, added if new. None once the 16-bit space is full: the text is then left unmarked. */
  readonly register: (entry: MarkerEntry) => Option.Option<number>;
  readonly get: (index: number) => Option.Option<MarkerEntry>;
  readonly clear: () => void;
  readonly size: () => number;
}

const entryKey = (entry: MarkerEntry) => `${entry.id}\u0000${JSON.stringify(entry.values ?? {})}`;

export const createMarkerRegistry = (capacity: number = MAX_MARKER_INDEX + 1): MarkerRegistry => {
  // Mutable on purpose: the registry is written on every formatted text in edit mode and must stay O(1).
  const entries: MarkerEntry[] = [];
  const byKey = new Map<string, number>();

  return {
    register: (entry) => {
      const key = entryKey(entry);
      const known = byKey.get(key);
      if (known !== undefined) return Option.some(known);
      if (entries.length >= capacity) return Option.none();
      const index = entries.length;
      entries.push(entry);
      byKey.set(key, index);
      return Option.some(index);
    },
    get: (index) => Option.fromNullable(entries[index]),
    clear: () => {
      entries.length = 0;
      byKey.clear();
    },
    size: () => entries.length,
  };
};

/** The app-wide registry the marked intl writes and the picker reads. Cleared on every edit-mode toggle. */
export const markerRegistry = createMarkerRegistry();
